/* eslint-disable react/prop-types */

import React, { useEffect, useState, useCallback } from 'react';
import vlog from '../../javascript/vlog';
import useHashtagSearch from './use_hashtag_search';
import SkeletonTable from './skeleton_table';
import FollowedHashtagsTable from './followed_hashtags_table';
import SearchHashtagsView from './search_hashtags_view';
import PaginationControls from './pagination_controls';
import TabNavigation from './tab_navigation';
import ConfigureSpamTolerance from './configure_spam_tolerance';
import setFollowStatus from './follow_actions';

function Following({
                     userId, profileId, authenticatedHeadersHash, setError,
                   }) {
  vlog.trace('Rendering Following component');

  const [myFollows, setMyFollows] = useState([]);
  const [myBlocked, setMyBlocked] = useState([]);
  const [myMuted, setMyMuted] = useState([]);
  const [hasMutedHashtags, setHasMutedHashtags] = useState(false);
  const [activeTab, setActiveTab] = useState('following');
  const [loading, setLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [spamTolerance, setSpamTolerance] = useState(null);
  const [hashtagUsageCeiling, setHashtagUsageCeiling] = useState(null);

  const {
    searchTerm,
    setSearchTerm,
    isSearching,
    searchResults,
    searchHashtags,
    clearSearch,
  } = useHashtagSearch(authenticatedHeadersHash, profileId);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const limit = 30;

  const checkMutedHashtags = useCallback(async () => {
    if (hashtagUsageCeiling === null) return;

    const maxUsage = hashtagUsageCeiling !== null ? hashtagUsageCeiling : 1000000;
    const url = `${window.V2_URL}/match_feed/api/profiles/${
      profileId
    }/followed_hashtag_searches?follow_status=mute&exclude_force_followed=true&max_usage_count=${
      maxUsage
    }`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: new Headers({
          ...authenticatedHeadersHash,
          'Content-Type': 'application/json',
        }),
      });

      if (response.ok) {
        const responseBody = await response.json();
        setHasMutedHashtags(responseBody.total > 0);
        vlog.debug(
          'Muted hashtags check:',
          responseBody.total > 0 ? 'Has muted hashtags' : 'No muted hashtags',
        );
      }
    } catch (err) {
      vlog.error('Error checking for muted hashtags:', err);
    }
  }, [hashtagUsageCeiling, profileId, authenticatedHeadersHash]);

  useEffect(() => {
    async function fetchHashtagUsageCeiling() {
      vlog.trace('Fetching hashtag usage ceiling with spamTolerance:', spamTolerance);
      const { supabaseClient } = window;
      const { data, err } = await supabaseClient
        .from('notification_configurations')
        .select('hashtag_usage_ceiling')
        .eq('user_id', userId)
        .single();

      if (err) {
        vlog.error('Error fetching hashtag usage ceiling:', err);
        // Use a default value if we can't get the ceiling
        setHashtagUsageCeiling(10000); // High default to ensure all hashtags load
      } else {
        vlog.debug('Fetched hashtag usage ceiling:', data.hashtag_usage_ceiling);
        setHashtagUsageCeiling(data.hashtag_usage_ceiling);
        // setHashtagUsageCeiling(1); // DONTCOMMIT useful for testing
      }
    }

    fetchHashtagUsageCeiling();
  }, [spamTolerance, userId]);

  const fetchActiveTab = useCallback(
    async (page = 1, forceReload = false) => {
      vlog.debug(
        'Fetching data for active tab:',
        activeTab,
        'at page:',
        page,
        'forceReload:',
        forceReload,
        'ceiling:',
        hashtagUsageCeiling,
      );

      // If we don't have the ceiling yet and this isn't forced, wait
      if (hashtagUsageCeiling === null && !forceReload) {
        vlog.debug('Skipping fetch - waiting for hashtag usage ceiling to be available');
        return;
      }

      if (!forceReload && isActionLoading) {
        return; // Prevent refresh if we're just toggling a switch
      }
      setIsActionLoading(true);

      let followStatus = '';
      let url = '';

      if (activeTab === 'following') {
        followStatus = 'follow';
      } else if (activeTab === 'blocked') {
        followStatus = 'block';
      } else if (activeTab === 'muted') {
        followStatus = 'follow'; // Use "follow" status for muted hashtags
      } else {
        vlog.warn('Unknown tab selected:', activeTab);
        return;
      }

      // Use a very high default if ceiling isn't available yet
      const maxUsage = hashtagUsageCeiling !== null ? hashtagUsageCeiling : 1000000;

      if (activeTab === 'muted') {
        url = `${window.V2_URL}/match_feed/api/profiles/${profileId}/followed_hashtag_searches?follow_status=mute&exclude_force_followed=true&max_usage_count=${maxUsage}&page=${page}&limit=${limit}`;
      } else if (activeTab === 'blocked') {
        url = `${window.V2_URL}/match_feed/api/profiles/${profileId}/followed_hashtag_searches?follow_status=block&page=${page}&limit=${limit}`;
      } else {
        url = `${window.V2_URL}/match_feed/api/profiles/${profileId}/followed_hashtag_searches?follow_status=${followStatus}&max_usage_count=${maxUsage}&page=${page}&limit=${limit}`;
      }

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: new Headers({
            ...authenticatedHeadersHash,
            'Content-Type': 'application/json',
          }),
        });

        if (response.ok) {
          const responseBody = await response.json();
          vlog.debug(
            'Fetched data for tab:',
            activeTab,
            'response size:',
            responseBody.data.length,
          );

          if (activeTab === 'following') setMyFollows(responseBody.data);
          if (activeTab === 'blocked') setMyBlocked(responseBody.data);
          if (activeTab === 'muted') {
            setMyMuted(responseBody.data);
            setHasMutedHashtags(responseBody.data.length > 0);
          }

          const totalItems = responseBody.total;
          setTotalPages(Math.ceil(totalItems / limit));
          vlog.trace('Total items:', totalItems, 'Total pages:', totalPages);

          if (activeTab !== 'muted') {
            checkMutedHashtags();
          }
        } else {
          const errorText = await response.text();
          vlog.warn('Network response was not ok for URL:', url, errorText);
          window.Qwoted.showToast({
            toastTitle: 'Error',
            toastBody: `Failed to load ${activeTab} hashtags: ${response.statusText}`,
            variant: 'danger',
          });
          throw new Error('Network response was not ok.');
        }
      } catch (err) {
        vlog.error('Error fetching follows for active tab:', err);
        window.Qwoted.showToast({
          toastTitle: 'Error',
          toastBody: `Failed to load ${activeTab} hashtags: ${err.message}`,
          variant: 'danger',
        });
      } finally {
        setLoading(false);
        setIsActionLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab, hashtagUsageCeiling],
  );

  // Handle actions for search results with appropriate refresh
  const handleSearchAction = async (status, type, id, forceFollow) => {
    await setFollowStatus({
      followableType: type,
      followableId: id,
      status,
      forceFollow,
      setIsActionLoading,
      authenticatedHeadersHash,
      profileId,
    });

    // If we're searching, refresh search results
    if (searchTerm.length >= 2) {
      searchHashtags(searchTerm);
    }
  };

  // Force a refresh when hashtagUsageCeiling changes
  useEffect(() => {
    if (hashtagUsageCeiling !== null) {
      vlog.debug(
        'Hashtag usage ceiling is now available:',
        hashtagUsageCeiling,
        '- refreshing data',
      );
      fetchActiveTab(currentPage, true);

      if (activeTab !== 'muted') {
        checkMutedHashtags();
      }
    }
  }, [hashtagUsageCeiling, fetchActiveTab, currentPage, activeTab, checkMutedHashtags]);

  const handleTabChange = (tab) => {
    vlog.debug('Tab changed to:', tab);
    setActiveTab(tab);
    setCurrentPage(1);
    setTotalPages(1);
  };

  // If user is on muted tab and it becomes empty, switch back to following tab
  useEffect(() => {
    if (activeTab === 'muted' && !hasMutedHashtags && myMuted.length === 0) {
      vlog.debug('No more muted hashtags found, switching to following tab');
      setActiveTab('following');
    }
  }, [activeTab, hasMutedHashtags, myMuted.length]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      vlog.trace('Page changed to:', newPage);
      setCurrentPage(newPage);
    } else {
      vlog.warn('Invalid page change attempt:', newPage);
    }
  };

  return (
    <div className="container-fluid mt-4 border-top pt-4">
      <div className="row position-relative">
        <div className="col-12 col-lg-9">
          <ConfigureSpamTolerance
            onChange={setSpamTolerance}
            userId={userId}
            authenticatedHeadersHash={authenticatedHeadersHash}
            setError={setError}
          />
        </div>
      </div>

      <div className="row position-relative">
        <div className="col-12 col-lg-9">
          <hr />
        </div>
      </div>

      <div className="row position-relative">
        <div className="col-12 col-lg-9">
          <h4 className="fw-bold my-4">Hashtags</h4>

          <div className="alert alert-primary fs-6 p-1">
            <table className="table table-borderless mb-0">
              <tbody>
                <tr>
                  <td
                    className="bg-transparent"
                    style={{ width: '40px', textAlign: 'center' }}
                  >
                    <i className="fa-solid fa-bell" />
                  </td>
                  <td className="bg-transparent ps-0">
                    <strong>Following:</strong> Receive notifications for these profile
                    hashtags.
                  </td>
                </tr>
                <tr>
                  <td
                    className="bg-transparent pt-0"
                    style={{ width: '40px', textAlign: 'center' }}
                  >
                    <i className="fa-solid fa-bell-slash" />
                  </td>
                  <td className="bg-transparent ps-0 pt-0">
                    <strong>Muted:</strong> You won&apos;t get alerts for these hashtags because they
                    are too noisy / high volume. If a hashtag you follow is also tagged, you&apos;ll
                    still receive notifications.
                  </td>
                </tr>
                <tr>
                  <td
                    className="bg-transparent pt-0"
                    style={{ width: '40px', textAlign: 'center' }}
                  >
                    <i className="fa-solid fa-ban" />
                  </td>
                  <td className="bg-transparent ps-0 pt-0">
                    <strong>Blocked:</strong> You won&apos;t receive alerts tagged with these, even if
                    other hashtags match.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="row position-relative">
        <div className="col-12 col-lg-9">
          <div className="mb-3">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fa-solid fa-search" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search and add hashtags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {isSearching && (
                <span className="input-group-text">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </span>
                </span>
              )}
              {searchTerm && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    clearSearch(() => {
                      // Reload the current tab data when exiting search
                      fetchActiveTab(currentPage, true);
                    });
                  }}
                >
                  <i className="fa-solid fa-times" />
                </button>
              )}
            </div>
          </div>

          {searchTerm.length >= 2 ? (
            <SearchHashtagsView
              searchResults={searchResults}
              isSearching={isSearching}
              searchTerm={searchTerm}
              handleFollow={(type, id, forceFollow) => {
                handleSearchAction('follow', type, id, forceFollow);
              }}
              handleMute={(type, id) => {
                handleSearchAction('mute', type, id);
              }}
              handleBlock={(type, id) => {
                handleSearchAction('block', type, id);
              }}
              hashtagUsageCeiling={hashtagUsageCeiling}
              profileId={profileId}
            />
          ) : (
            <>
              <TabNavigation
                activeTab={activeTab}
                onTabChange={handleTabChange}
                isLoading={isActionLoading}
                tabs={['following', ...(hasMutedHashtags ? ['muted'] : []), 'blocked']}
              />

              {loading ? (
                <div className="my-4">
                  <SkeletonTable />
                </div>
              ) : (
                <div className="card border-0 shadow-sm">
                  {isActionLoading && (
                    <div
                      className="spinner-border"
                      role="status"
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '10px',
                        width: '1.5rem',
                        height: '1.5rem',
                      }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}

                  <div
                    style={{
                      pointerEvents: isActionLoading ? 'none' : 'auto',
                      opacity: isActionLoading ? 0.5 : 1,
                    }}
                  >
                    {activeTab === 'following' && (
                      <FollowedHashtagsTable
                        follows={myFollows}
                        handleMute={(type, id) => setFollowStatus({
                          followableType: type,
                          followableId: id,
                          status: 'mute',
                          setIsActionLoading,
                          callback: fetchActiveTab,
                          currentPage,
                          authenticatedHeadersHash,
                          profileId,
                        })}
                        handleRemoveForceFollow={(type, id) => setFollowStatus({
                          followableType: type,
                          followableId: id,
                          forceFollow: false,
                          setIsActionLoading,
                          callback: fetchActiveTab,
                          currentPage,
                          onlyUpdateForceFollow: true,
                          authenticatedHeadersHash,
                          profileId,
                        })}
                        handleBlock={(type, id) => setFollowStatus({
                          followableType: type,
                          followableId: id,
                          status: 'block',
                          setIsActionLoading,
                          callback: fetchActiveTab,
                          currentPage,
                          authenticatedHeadersHash,
                          profileId,
                        })}
                        hashtagUsageCeiling={hashtagUsageCeiling}
                      />
                    )}

                    {activeTab === 'muted' && (
                      <>
                        <div className="alert alert-info mt-3 m-2">
                          <i className="fa-solid fa-info-circle me-2" />
                          <strong>Muted hashtags</strong>: These hashtags were automatically muted because they generate
                          too many notifications for your current notification frequency setting. If you follow them,
                          they may flood your inbox.
                        </div>
                        <FollowedHashtagsTable
                          follows={myMuted}
                          handleFollow={(type, id, forceFollow) => setFollowStatus({
                            followableType: type,
                            followableId: id,
                            status: 'follow',
                            forceFollow,
                            setIsActionLoading,
                            callback: fetchActiveTab,
                            currentPage,
                            authenticatedHeadersHash,
                            profileId,
                          })}
                          handleRemoveForceFollow={(type, id) => setFollowStatus({
                            followableType: type,
                            followableId: id,
                            forceFollow: false,
                            setIsActionLoading,
                            callback: fetchActiveTab,
                            currentPage,
                            onlyUpdateForceFollow: true,
                            authenticatedHeadersHash,
                            profileId,
                          })}
                          handleBlock={(type, id) => setFollowStatus({
                            followableType: type,
                            followableId: id,
                            status: 'block',
                            setIsActionLoading,
                            callback: fetchActiveTab,
                            currentPage,
                            authenticatedHeadersHash,
                            profileId,
                          })}
                          hashtagUsageCeiling={hashtagUsageCeiling}
                        />
                      </>
                    )}

                    {activeTab === 'blocked' && (
                      <FollowedHashtagsTable
                        follows={myBlocked}
                        handleFollow={(type, id, forceFollow) => setFollowStatus({
                          followableType: type,
                          followableId: id,
                          status: 'follow',
                          forceFollow,
                          setIsActionLoading,
                          callback: fetchActiveTab,
                          currentPage,
                          authenticatedHeadersHash,
                          profileId,
                        })}
                        handleMute={(type, id) => setFollowStatus({
                          followableType: type,
                          followableId: id,
                          status: 'mute',
                          setIsActionLoading,
                          callback: fetchActiveTab,
                          currentPage,
                          authenticatedHeadersHash,
                          profileId,
                        })}
                        handleRemoveForceFollow={(type, id) => setFollowStatus({
                          followableType: type,
                          followableId: id,
                          forceFollow: false,
                          setIsActionLoading,
                          callback: fetchActiveTab,
                          currentPage,
                          onlyUpdateForceFollow: true,
                          authenticatedHeadersHash,
                          profileId,
                        })}
                        hashtagUsageCeiling={hashtagUsageCeiling}
                      />
                    )}

                    {totalPages > 1 && (
                      <PaginationControls
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        isLoading={isActionLoading}
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Following;
